/**
 * Thin client that wraps the Netlify Functions. The browser never speaks to
 * Supabase directly for *write* operations on student data — that path goes
 * through these endpoints, which run server-side with the service role key
 * and enforce server-side scoring + duplicate-answer guards.
 *
 * Public *read* paths (leaderboard, schools list, active questions) go through
 * the Supabase JS client + RLS, see /src/lib/leaderboard.ts and
 * /src/lib/texasSchools.ts.
 */

import type {
  CompleteGameSessionResult,
  RegisterStudentInput,
  RegisterStudentResult,
  StartGameSessionResult,
  SubmitAnswerInput,
  SubmitAnswerResult,
} from "@/types/game";
import type { ParsedQuestionDocResult } from "@/types/questions";
import type { LeaderboardFilters, LeaderboardPayload } from "@/types/leaderboard";

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/.netlify/functions";

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  const txt = await res.text();
  let data: unknown = null;
  try { data = txt ? JSON.parse(txt) : null; } catch { data = txt; }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export const api = {
  registerStudent: (input: RegisterStudentInput) =>
    call<RegisterStudentResult>("registerStudent", { body: JSON.stringify(input) }),

  startGameSession: (student_id: string) =>
    call<StartGameSessionResult>("startGameSession", { body: JSON.stringify({ student_id }) }),

  submitAnswer: (input: SubmitAnswerInput) =>
    call<SubmitAnswerResult>("submitAnswer", { body: JSON.stringify(input) }),

  completeGameSession: (game_session_id: string) =>
    call<CompleteGameSessionResult>("completeGameSession", { body: JSON.stringify({ game_session_id }) }),

  uploadQuestionDocx: async (file: File): Promise<ParsedQuestionDocResult> => {
    // Keep payloads small — base64 inside JSON is fine for typical question banks.
    const buf = await file.arrayBuffer();
    const b64 = arrayBufferToBase64(buf);
    return call<ParsedQuestionDocResult>("uploadQuestionDocx", {
      body: JSON.stringify({ filename: file.name, base64: b64 }),
    });
  },

  publishQuestionSet: (payload: {
    title: string;
    description?: string;
    questions: ParsedQuestionDocResult["questions"];
  }) =>
    call<{ question_set_id: string; question_count: number }>("publishQuestionSet", {
      body: JSON.stringify(payload),
    }),

  getLeaderboard: (filters: LeaderboardFilters = {}) =>
    call<LeaderboardPayload>("getLeaderboard", { body: JSON.stringify(filters) }),

  adminExportCsv: (kind: "students" | "sessions" | "answers" | "leaderboard" | "event_leaderboard", extra: { event_id?: string } = {}) =>
    fetch(`${BASE}/adminExportCsv`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ kind, ...extra }),
    }),
};

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}
